import _ from 'lodash'
import Backbone from 'backbone'
import TextArea from 'scoreboard/components/textarea'
import keyCodes from 'scoreboard/components/key-codes'

/**
 * A textarea designed for working with SQL queries. To handle re-rendering, this
 * uses a {@link HistoryManager} to keep track of all changes to allow for undo/redo
 * functionality. The calling view is expected to create and pass in the manager.
 * This should be cached and re-used for new renders to preserve the textarea state.
 *
 * This component also supports some conveniences for working with SQL:
 *
 * * Pressing CMD/CTRL + ENTER will execute the current command
 * * Pressing CMD + Z/CMD + SHIFT + Z will perform undo/redo operations
 * * If some sql is highlighted, only this code will run
 * * If a command ends in a semi-colon, only the command terminated by the semi-colon will run
 *
 * If an external button is added to execute queries, have the click handler call
 * {@link SqlEditor#execute} to ensure the correct sql is executed
 */
const SqlEditor = Backbone.View.extend({
	events: {
		'keydown': '_onKeydown',
		'input': '_onInput'
	},

	/** Tracks all changes made in the textarea */
	historyManager: undefined,

	/**
	 *
	 * @param options
	 * @param {HistoryManager} [options.manager] an instance of a HistoryManager
	 * @param {(string) => void} [options.onExecute] executes the sql in the editor
	 **/
	initialize: function(options) {
		this.options = options
		this.historyManager = options.manager
		return this
	},

	/**
	 *
	 * @param historyManager When re-rendering,
	 * @return {SqlEditor}
	 */
	render: function() {
		this.textarea = new TextArea({ el: this._editor() }).render()
		this.setVal(this.options.manager.history[this.options.manager.historyIndex] || '', true, false)

		this._editor()[0].focus()
		const selectedRange = this.historyManager.getSelectedRange()
		if (selectedRange) {
			this._editor()[0].setSelectionRange(selectedRange.start, selectedRange.end)
		}
		return this
	},

	execute: function() {
		this.historyManager.setSelectedRange(this._editor()[0])
		this.options.onExecute && this.options.onExecute(this._getSql())
		process.nextTick(() => this.focus())
	},

	focus: function() {
		const selectionRange = this._getSelectionRange()
		this._editor()[0].focus()
		this._editor()[0].selectionStart = selectionRange.start
		this._editor()[0].selectionEnd = selectionRange.end
	},

	getVal: function() {
		return this._editor().val()
	},

	/**
	 * Set the value of the textarea and reset the change history
	 *
	 * @param silent true to fire a change event; false to skip the event
	 */
	setVal: function(val, silent = false, reset = true) {
		this._editor().val(val)
		if (!silent) {
			this.trigger('change', this.getVal())
		}
		if (reset) {
			this.historyManager.reset(val)
		}
	},

	_onKeydown(e) {
		if (e.metaKey) {
			if (e.which === keyCodes.ENTER) {
				e.preventDefault()
				e.stopPropagation()
				this.execute()
			}
			if (e.which === keyCodes.Z) {
				e.preventDefault()
				e.stopPropagation()
				const newVal = this.historyManager.shift(e.shiftKey ? +1 : -1)
				if (!_.isNil(newVal) && this.getVal() !== newVal) {
					this.setVal(newVal, false, false)
				}
			}
		}
	},

	_onInput: function() {
		this.historyManager.add(this.getVal())
		this.trigger('change', this.getVal())
	},

	_getSql: function() {
		const allText = this.getVal()

		// If there is any highlighted text, run it as-is
		const selectedRange = this._getSelectionRange()
		if (selectedRange.start !== selectedRange.end) {
			return allText
				.substr(selectedRange.start, selectedRange.end - selectedRange.start)
				.trim()
		}

		// If a semi-colon is present, run everything starting from the last
		// semi-colon (or the beginning of the script) to the next semi-colon
		// (or the end of the script
		let start, end, nextIndex

		if (allText.charAt(selectedRange.start - 1) === ';') {
			// The caret is placed directly after a semi-colon
			end = selectedRange.start - 1
		} else {
			// We need to search for the next semi-colon that ends this statement
			nextIndex = selectedRange.start
			while (nextIndex < allText.length) {
				if (allText.charAt(nextIndex) === ';') {
					break
				}
				nextIndex++
			}
			end = nextIndex
		}

		// Search from the end, back through the string until we find another
		// semi-colon or the beginning of the string
		nextIndex = end - 1
		while (nextIndex >= 0) {
			if (allText.charAt(nextIndex) === ';') {
				break
			}
			nextIndex--
		}
		// If we found a previous semi-colon start at the character AFTER that
		start = nextIndex ? nextIndex + 1 : 0

		return allText.substring(start, end).trim()
	},

	_editor: function() {
		return this.$el
	},

	_getSelectionRange: function() {
		return {
			start: this._editor()[0].selectionStart,
			end: this._editor()[0].selectionEnd
		}
	},

	remove: function() {
		Backbone.prototype.remove.apply(this, arguments)
		_.invoke(this.textarea, 'remove')
		this.textarea.remove()
	}
})

export default SqlEditor
SqlEditor.__filename__ = 'components/sql-console/sql-editor.js'
